<script setup lang="ts">
import { computed, ref, Ref, UnwrapRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/api';
import { trackingSurveyEvent } from '@/api/tracking';
import {
  FIRST_PUBLISHED_AT,
  PRODUCT_NAME,
  searchPresetActivity,
  SURVEY_SEARCH_DEBOUNCE_WAIT_MS,
} from '@/constants';
import { DgrSelectbox } from '@stockmarkteam/donguri-ui';
import DropdownDateRangeCalendar from '@/components/common/dropdown-date-range-calendar.vue';
import SearchBar from '@/components/common/molecules/search-bar.vue';
import SwrScrollingPagination from '@/components/common/swr-scrolling-pagination.vue';
import ShareSurveysListHeader from '@/components/share-survey/containers/share-surveys-list-header.vue';
import SurveyCard from '@/components/survey/survey-card.vue';
import {
  SearchDate,
  SharedType,
  ShareSurveySearchResponse,
  UserEntity,
} from '@/types';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { getToday } from '@/utils/dates';
import { debounce } from '@/utils/debounce';
import { formatDate } from '@/utils/formatters';
import {
  SHARE_SURVEYS_SEARCH_NOT_SHARED_PAGE_NAME,
  SHARE_SURVEYS_SEARCH_SHARED_BY_ME_PAGE_NAME,
  SHARE_SURVEYS_SEARCH_SHARED_BY_OTHERS_PAGE_NAME,
  SURVEY_SEARCH_PAGE_LIMIT,
} from '@/utils/survey/constants';
import { trackSurveyPageView } from '@/utils/survey/tracking';
import { useShareSurveys, useUserInfo } from '@/utils/swr';
import { formatDateForTracking } from '@/utils/trackingHelper';

const searchResultTotalCount = ref(0);

const { getCurrentPathForTracking } = useCurrentPathForTracking();

// 検索条件
const searchKeyword = ref(''); // 検索キーワード
const searchKeywordForApi = ref<string>('');
const searchOwnerId = ref<number | undefined>(); // 作成者のID
const searchSampleOnly = ref(false); // 作成者がAnews
const searchExcludeOwner = ref(false); // 指定した作成者IDを除外
const searchShared = ref<SharedType>('both'); // 共有ユーザーがいる/いない/両方で絞り込む
const searchStartDate = ref<SearchDate | undefined>(); // 開始日
const searchEndDate = ref<SearchDate | undefined>(); // 終了日

// 計測用
const searchPeriod = ref<string>('all'); // 検索期間のプリセット

// 作成者の選択肢
const selectedOptionValue = ref<string>();
const owners = ref<Array<UserEntity>>([]);

const { data: userInfo } = useUserInfo();
const route = useRoute();

const userOptions = computed(() => {
  const defaultOptions = [
    { label: 'すべての作成者', value: 'none' },
    { label: PRODUCT_NAME, value: PRODUCT_NAME },
  ];
  const options =
    owners.value
      .filter(user => user.id !== userInfo.value?.id)
      .map(user => ({
        label: user.user_name,
        value: String(user.id),
      })) || [];
  return defaultOptions.concat(options);
});

const fetchShareSurveyOwners = async () => {
  owners.value = await api.fetchShareSurveyOwners();
};

const searchConditionKey = computed(() => {
  return [
    searchKeywordForApi.value,
    searchOwnerId.value,
    searchSampleOnly.value,
    searchExcludeOwner.value,
    searchShared.value,
    searchStartDate.value,
    searchEndDate.value,
  ].join('-');
});

watch(searchKeyword, newKeyword => {
  debouncedUpdateKeyword(newKeyword);
});

const applyDate = (option: {
  presetKey: string;
  value: { from: SearchDate; to: SearchDate };
}) => {
  searchStartDate.value = option.value.from;
  searchEndDate.value = option.value.to;
  searchPeriod.value = option.presetKey;
};

const debouncedUpdateKeyword = debounce((keyword: string) => {
  searchKeywordForApi.value = keyword;
}, SURVEY_SEARCH_DEBOUNCE_WAIT_MS);

const fetchShareSurvey = (data: ShareSurveySearchResponse, page: number) => {
  searchResultTotalCount.value = data.total_count;

  const offset = (page - 1) * SURVEY_SEARCH_PAGE_LIMIT;
  trackShareSurveySearch(offset, SURVEY_SEARCH_PAGE_LIMIT);
};

const paginationFunc = (pageRef: Ref<number>, pageLimit: number) =>
  useShareSurveys(
    searchKeywordForApi,
    searchOwnerId,
    searchSampleOnly,
    searchExcludeOwner,
    searchShared,
    searchStartDate,
    searchEndDate,
    pageRef,
    pageLimit,
  );

const dataAccessor = (
  data: UnwrapRef<ReturnType<typeof useShareSurveys>['data']>,
) => {
  return data?.share_surveys;
};

const ownerTypeForTracking = computed(() => {
  if (searchSampleOnly.value) return 'sample';
  if (searchOwnerId.value) return 'user';
  return undefined;
});

const shouldIncludeOwnerInfoInTracking = computed(() => {
  return (
    currentPageName.value === SHARE_SURVEYS_SEARCH_SHARED_BY_OTHERS_PAGE_NAME &&
    ownerTypeForTracking.value &&
    !searchExcludeOwner.value
  );
});

const trackShareSurveySearch = (offset: number, limit: number) => {
  trackingSurveyEvent('share_surveys_view', {
    page: {
      name: currentPageName.value,
      url: getCurrentPathForTracking(),
    },
    request: {
      offset,
      limit,
      period: searchPeriod.value,
      period_detail:
        searchPeriod.value === 'custom' &&
        searchStartDate.value &&
        searchEndDate.value
          ? {
              from: formatDateForTracking(searchStartDate.value),
              to: formatDateForTracking(searchEndDate.value),
            }
          : undefined,
      owner: shouldIncludeOwnerInfoInTracking.value
        ? {
            type: ownerTypeForTracking.value,
            user_id: searchOwnerId.value,
          }
        : undefined,
    },
  });
};

watch(selectedOptionValue, value => {
  switch (value) {
    case 'none':
      searchOwnerId.value = userInfo.value?.id;
      searchSampleOnly.value = false;
      searchExcludeOwner.value = true;
      break;
    case PRODUCT_NAME:
      searchOwnerId.value = undefined;
      searchSampleOnly.value = true;
      searchExcludeOwner.value = false;
      break;
    default:
      searchOwnerId.value = Number(value);
      searchSampleOnly.value = false;
      searchExcludeOwner.value = false;
  }
});

const currentPageName = computed(() => {
  switch (route.name) {
    case 'sharedByOthers':
      return SHARE_SURVEYS_SEARCH_SHARED_BY_OTHERS_PAGE_NAME;
    case 'sharedByMe':
      return SHARE_SURVEYS_SEARCH_SHARED_BY_ME_PAGE_NAME;
    case 'notShared':
      return SHARE_SURVEYS_SEARCH_NOT_SHARED_PAGE_NAME;
    default:
      return SHARE_SURVEYS_SEARCH_SHARED_BY_OTHERS_PAGE_NAME;
  }
});

watch(
  [() => route.name, userInfo],
  () => {
    searchResultTotalCount.value = 0;

    if (!userInfo.value?.id) return;
    switch (route.name) {
      case 'sharedByOthers':
        fetchShareSurveyOwners();
        searchExcludeOwner.value = true;
        searchOwnerId.value = userInfo.value?.id;
        searchShared.value = 'both';
        trackSurveyPageView(currentPageName.value, getCurrentPathForTracking());
        break;
      case 'sharedByMe':
        searchExcludeOwner.value = false;
        searchOwnerId.value = userInfo.value?.id;
        searchShared.value = 'with';
        trackSurveyPageView(currentPageName.value, getCurrentPathForTracking());
        break;
      case 'notShared':
        searchExcludeOwner.value = false;
        searchOwnerId.value = userInfo.value?.id;
        searchShared.value = 'without';
        trackSurveyPageView(currentPageName.value, getCurrentPathForTracking());
        break;
      default:
        break;
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="share-surveys-container">
    <ShareSurveysListHeader />
    <div class="share-surveys-content">
      <div class="search-result-count">
        <div class="c-title-bold--m">シェアスレッド</div>
        <div class="count-text c-text-regular--s">
          ・{{ searchResultTotalCount }}件
        </div>
      </div>
      <div class="search-condition">
        <SearchBar placeholder="シェアスレッドを検索" v-model="searchKeyword" />
        <div class="search-condition-select-box">
          <DgrSelectbox
            v-if="route.name === 'sharedByOthers'"
            :options="userOptions"
            size="small"
            placeholder="すべての作成者"
            v-model="selectedOptionValue"
          />
          <DropdownDateRangeCalendar
            :range="{ from: searchStartDate, to: searchEndDate }"
            :preset="searchPresetActivity"
            :min="FIRST_PUBLISHED_AT"
            :max="getToday()"
            :is-open="true"
            :disabled="false"
            :font-size="'small'"
            :icon-size="'small'"
            @apply-date="applyDate"
          />
        </div>
      </div>
      <div class="search-result">
        <SwrScrollingPagination
          :key="searchConditionKey"
          :page-limit="SURVEY_SEARCH_PAGE_LIMIT"
          :pagination-func="paginationFunc"
          :data-accessor="dataAccessor"
          @fetch="fetchShareSurvey"
        >
          <template v-slot="{ items }">
            <div
              class="item"
              v-for="shareSurvey in items"
              :key="shareSurvey.uuid"
            >
              <router-link
                :to="{
                  name: 'shareSurvey',
                  params: { uuid: shareSurvey.uuid },
                }"
              >
                <SurveyCard
                  :key="shareSurvey.uuid"
                  :highlight-keywords="[searchKeywordForApi]"
                  :title="shareSurvey.title"
                  :content="shareSurvey.content || ''"
                  :user-name="shareSurvey.owner.user_name"
                  :user-icon-url="shareSurvey.owner.image_url || ''"
                  :use-anews-icon="!shareSurvey.owner.image_url"
                  :updated-at="formatDate(shareSurvey.updated_at)"
                />
              </router-link>
              <div class="spacing-08" />
            </div>
          </template>
        </SwrScrollingPagination>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.share-surveys-content {
  max-width: var(--survey-session-width);
  margin: 0 auto;
  padding: 0 32px;
}
.search-result-count {
  display: flex;
  align-items: center;
  padding: 24px 0;
  gap: 4px;
  .count-text {
    color: $color-gray800;
  }
}
.search-condition {
  display: flex;
  flex-direction: column;
  gap: 16px;
  .search-condition-select-box {
    display: flex;
    gap: 16px;
  }
}
.search-result {
  padding: 16px 0;
  .item {
    cursor: pointer;
  }
}
</style>
